import { useEffect, useRef } from 'react';
import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import type { AskPageContext } from '@shared/designVariants/DesignWorkspaceProvider';
import { CampaignPicker } from './CampaignPicker';
import { OperationJournal } from './OperationJournal';
import { OperationActionReview } from './OperationActionReview';
import { useAskConversation } from './AskConversationProvider';
import {
  containsSensitiveAssistantInput,
  sensitiveAssistantInputMessage,
} from './assistantSensitiveInput';
import {
  useAskWorkspaceMutation,
  useConfirmWorkspaceOperationMutation,
  useWorkspaceModelsQuery,
  useWorkspaceOperationsQuery,
  type AssistantMessage,
  type WorkspaceOperation,
} from './workspaceApi';

export const AskWorkspace = ({
  onDirtyChange,
  writesBlocked = false,
  pageContext = null,
}: {
  onDirtyChange: (v: boolean) => void;
  writesBlocked?: boolean;
  pageContext?: AskPageContext | null;
}) => {
  const writeBlock = useRef(writesBlocked);
  writeBlock.current = writesBlocked;
  const { state, setState, generation, busy, lastRequest } = useAskConversation();
  const { campaign, provider, model, messages, text, submittedOperation, error, working } = state;
  const setProvider = (provider: string) => setState((old) => ({ ...old, provider }));
  const setModel = (model: string) => setState((old) => ({ ...old, model }));
  const setMessages = (messages: AssistantMessage[]) => setState((old) => ({ ...old, messages }));
  const setText = (text: string) => setState((old) => ({ ...old, text }));
  const setOperation = (submittedOperation: WorkspaceOperation | null) =>
    setState((old) => ({ ...old, submittedOperation }));
  const setError = (error: string) => setState((old) => ({ ...old, error }));
  const pending =
    !!submittedOperation && ['prepared', 'queued', 'running'].includes(submittedOperation.state);
  // Shares the journal subscription/cache; confirmation acknowledges queuing, not completion.
  const journal = useWorkspaceOperationsQuery(
    {},
    {
      skip: !pending,
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );
  const current = journal.currentData?.find((row) => row.id === submittedOperation?.id);
  const canSync =
    pending &&
    current &&
    !['planning', 'prepared'].includes(current.state) &&
    !(submittedOperation?.state === 'running' && current.state === 'queued');
  // A cached prepared row must not undo a just-confirmed or canceled decision.
  const operation = canSync ? current : submittedOperation;
  const proposalWrites = !!operation?.actions.some((action) => action.kind !== 'campaign-summary');
  const models = useWorkspaceModelsQuery();
  const [ask, { isLoading }] = useAskWorkspaceMutation();
  const [confirm, { isLoading: confirming }] = useConfirmWorkspaceOperationMutation();
  const send = async () => {
    if (
      busy.current ||
      !campaign ||
      !model ||
      !provider ||
      !text.trim() ||
      operation?.state === 'prepared'
    )
      return;
    if (containsSensitiveAssistantInput(text)) {
      setText('');
      setError(sensitiveAssistantInputMessage);
      return;
    }
    busy.current = true;
    const startedGeneration = generation.current;
    setState((old) => ({ ...old, working: true }));
    setError('');
    const next: AssistantMessage[] = [...messages, { role: 'user', content: text.trim() }];
    const input = {
      campaignId: campaign.id,
      providerId: provider,
      modelId: model,
      messages: next.slice(-12),
    };
    const key = JSON.stringify(input);
    if (lastRequest.current?.key !== key) lastRequest.current = { key, id: crypto.randomUUID() };
    try {
      const response = await ask({
        ...input,
        requestId: lastRequest.current.id,
      }).unwrap();
      if (generation.current !== startedGeneration) return;
      lastRequest.current = null;
      setMessages([...next, { role: 'assistant', content: response.answer }]);
      setText('');
      setOperation(response.operation);
    } catch (e) {
      if (generation.current !== startedGeneration) return;
      setError(getApiErrorMessage(e, 'Unable to get a model answer. No new action was confirmed.'));
    } finally {
      busy.current = false;
      if (generation.current === startedGeneration) setState((old) => ({ ...old, working: false }));
    }
  };
  const decide = async (cancel: boolean) => {
    if (!operation || busy.current) return;
    if (
      !cancel &&
      writeBlock.current &&
      operation.actions.some((action) => action.kind !== 'campaign-summary')
    ) {
      setError('Save or discard the current workspace edits before confirming changes.');
      return;
    }
    busy.current = true;
    const startedGeneration = generation.current;
    setState((old) => ({ ...old, working: true }));
    try {
      const result = await confirm({ id: operation.id, cancel }).unwrap();
      if (generation.current !== startedGeneration) return;
      setOperation(result);
      setError('');
    } catch (e) {
      if (generation.current !== startedGeneration) return;
      setError(
        getApiErrorMessage(e, 'Unable to confirm. Check the run journal before trying again.'),
      );
    } finally {
      busy.current = false;
      if (generation.current === startedGeneration) setState((old) => ({ ...old, working: false }));
    }
  };
  useEffect(() => {
    // Conversation drafts and prepared proposals survive workspace navigation in the scoped owner.
    onDirtyChange(isLoading || confirming || working);
    return () => onDirtyChange(false);
  }, [isLoading, confirming, working, onDirtyChange]);
  const locked = isLoading || confirming || working || operation?.state === 'prepared';
  const seenPageCampaign = useRef<string | undefined>(undefined);
  const pageCampaign = pageContext?.campaign;
  useEffect(() => {
    const id = pageCampaign?.id;
    if (!id || seenPageCampaign.current === id) return;
    seenPageCampaign.current = id;
    if (!locked && !campaign && !messages.length && !text && !operation) {
      setState((old) => ({ ...old, campaign: { id, title: pageCampaign.title } }));
    }
  }, [pageCampaign, locked, campaign, messages.length, text, operation, setState]);
  const contextSuggestions = pageContext?.job
    ? [
        'Summarize the saved campaign and its agent assignments.',
        'What does the saved campaign summary tell us about its configuration?',
      ]
    : pageContext?.page === 'Call history' || pageContext?.page === 'Analytics'
      ? [
          'Summarize the saved campaign state.',
          'Which saved campaign settings should I review before interpreting call results?',
        ]
      : [
          'Summarize the saved campaign state.',
          'Which saved campaign settings should I review before starting?',
        ];
  return (
    <Stack gap={2}>
      <Alert severity="info">
        Ask about a campaign or prepare a rename, start, stop or webhook action. Review each
        proposed action before confirming. Your selected company model receives this conversation
        and the campaign summary.
      </Alert>
      {pageContext && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">Current page: {pageContext.page}</Typography>
          {pageCampaign && <Typography variant="body2">Campaign: {pageCampaign.title}</Typography>}
          {pageContext.agent && (
            <Typography variant="body2">Agent: {pageContext.agent.name}</Typography>
          )}
          {pageContext.job && <Typography variant="body2">Job: {pageContext.job.name}</Typography>}
          <Typography variant="caption" color="text.secondary">
            Page labels help you choose context. Only the selected campaign summary and this
            conversation are sent; page drafts and Job instructions are not included.
          </Typography>
          {pageCampaign && campaign?.id !== pageCampaign.id && (
            <Button
              disabled={locked}
              onClick={() => {
                generation.current++;
                lastRequest.current = null;
                setState((old) => ({
                  ...old,
                  campaign: { id: pageCampaign.id, title: pageCampaign.title },
                  messages: [],
                  text: '',
                  submittedOperation: null,
                  error: '',
                }));
              }}>
              New conversation for current campaign
            </Button>
          )}
        </Paper>
      )}
      <CampaignPicker
        value={campaign}
        disabled={locked}
        onChange={(value) => {
          generation.current++;
          lastRequest.current = null;
          setState((old) => ({
            ...old,
            campaign: value,
            messages: [],
            text: '',
            submittedOperation: null,
            error: '',
          }));
        }}
      />
      <Stack direction="row" gap={2} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          This conversation stays available while you navigate. Reloading or switching accounts
          clears it.
        </Typography>
        <Button
          disabled={locked}
          onClick={() => {
            generation.current++;
            lastRequest.current = null;
            setState((old) => ({
              ...old,
              messages: [],
              text: '',
              submittedOperation: null,
              error: '',
            }));
          }}>
          New conversation
        </Button>
      </Stack>
      {models.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => void models.refetch()}>Retry</Button>}>
          Unable to load company models.
        </Alert>
      )}
      {!models.isLoading && models.data?.length === 0 && (
        <Alert severity="warning">
          No company model has server credentials and a supported endpoint. Configure an
          OpenAI-compatible provider and model through the current company settings.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
        <TextField
          select
          label="Company provider"
          value={provider}
          disabled={locked}
          fullWidth
          onChange={(e) => {
            setProvider(e.target.value);
            setModel('');
          }}>
          {!models.data?.length && (
            <MenuItem value="" disabled>
              No configured providers
            </MenuItem>
          )}
          {models.data?.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.title}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Model"
          value={model}
          fullWidth
          disabled={locked || !provider}
          onChange={(e) => setModel(e.target.value)}>
          {!provider && (
            <MenuItem value="" disabled>
              Select a provider first
            </MenuItem>
          )}
          {models.data
            ?.find((p) => p.id === provider)
            ?.models.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.title}
              </MenuItem>
            ))}
        </TextField>
      </Stack>
      {messages.map((m, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2, whiteSpace: 'pre-wrap' }}>
          <Typography variant="overline">{m.role === 'user' ? 'You' : 'Ask Callevate'}</Typography>
          <Typography>{m.content}</Typography>
        </Paper>
      ))}
      {error && <Alert severity="error">{error}</Alert>}
      {operation && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6">
            {operation.state === 'prepared'
              ? 'Review proposed actions'
              : `Action ${operation.state}`}
          </Typography>
          <OperationActionReview actions={operation.actions} snapshots={operation.snapshots} />
          {writesBlocked && proposalWrites && operation.state === 'prepared' && (
            <Alert severity="warning">
              Save or discard the current workspace edits before confirming changes.
            </Alert>
          )}
          {operation.state === 'prepared' && (
            <>
              <Typography variant="body2">
                Expires {new Date(operation.expires_at!).toLocaleTimeString()}. Confirmation
                authorizes only these exact actions. Live permission and target checks run again.
              </Typography>
              <Stack direction="row" gap={1}>
                <Button
                  variant="contained"
                  disabled={confirming || working || (writesBlocked && proposalWrites)}
                  onClick={() => void decide(false)}>
                  Confirm and execute
                </Button>
                <Button disabled={confirming || working} onClick={() => void decide(true)}>
                  Cancel proposal
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      )}
      <TextField
        label="Ask Callevate"
        multiline
        minRows={3}
        value={text}
        disabled={locked}
        slotProps={{ htmlInput: { maxLength: 4000 } }}
        onChange={(e) => {
          if (containsSensitiveAssistantInput(e.target.value)) {
            setText('');
            setError(sensitiveAssistantInputMessage);
          } else setText(e.target.value);
        }}
      />
      <Stack direction="row" gap={1} flexWrap="wrap" aria-label="Suggested campaign questions">
        {contextSuggestions.map((suggestion) => (
          <Button
            key={suggestion}
            size="small"
            variant="outlined"
            disabled={locked || !campaign}
            onClick={() => setText(suggestion)}>
            {suggestion}
          </Button>
        ))}
      </Stack>
      <Button
        variant="contained"
        disabled={locked || !campaign || !model || !text.trim()}
        onClick={() => void send()}>
        {isLoading ? 'Preparing answer…' : 'Send'}
      </Button>
      <OperationJournal writesBlocked={writesBlocked} />
    </Stack>
  );
};
